import React, { useState } from "react";
import { getRemitaPublicConfig } from "../services/api";
import { useNavigate } from "react-router-dom";
import logo from "../assets/logo2.png";
import axios from "axios";
import { FaSearch, FaSpinner, FaCheckCircle, FaClock, FaTimesCircle, FaArrowLeft, FaRedo } from "react-icons/fa";
import { loadScript } from "../utils/loadScript";

const getItemName = (item) => item?.service?.name || item?.name || "Service";

export default function TrackTransaction() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState("");
  const [selected, setSelected] = useState(null);
  const [requeryingRRR, setRequeryingRRR] = useState(null);

  const openRemitaWithRRR = async (tx) => {
    const rrr = tx.rrr;
    const amount = parseFloat(tx.invoice_amount || tx.total_amount || tx.amount || 0);
    if (!rrr || !amount) return;
    setRequeryingRRR(rrr);
    try {
      if (!window.RmPaymentEngine || typeof window.RmPaymentEngine.init !== "function") {
        await loadScript("https://login.remita.net/payment/v1/remita-pay-inline.bundle.js");
        await new Promise(r => setTimeout(r, 500));
      }
      const retryTransactionId = `${tx.reference || rrr}-r${Date.now()}`;
      const remitaConfig = await getRemitaPublicConfig();
      const paymentEngine = window.RmPaymentEngine.init({
        key: remitaConfig.public_key,
        processRrr: true,
        transactionId: retryTransactionId,
        customerId: tx.customer_meta?.payer_email || tx.customer_meta?.patient_email || "",
        firstName: tx.customer_meta?.payer_first_name || tx.customer_meta?.patient_first_name || "",
        lastName: tx.customer_meta?.payer_last_name || tx.customer_meta?.patient_last_name || "",
        email: tx.customer_meta?.payer_email || tx.customer_meta?.patient_email || "",
        amount,
        narration: `Payment retry for ${tx.reference || rrr}`,
        extendedData: { customFields: [{ name: "rrr", value: rrr }] },
        onSuccess: async (data) => {
          try {
            await axios.get(
              `https://api.uchselfservice.net/api/express/callback/remita/?transaction_id=${tx.reference || data.transactionId}&rrr=${rrr}&amount=${amount}`
            );
          } catch (_) {}
          alert("Payment successful! Reference: " + (tx.reference || rrr));
          window.location.reload();
        },
        onError: () => { alert("Payment failed. Please try again."); },
        onClose: () => {},
      });
      paymentEngine.showPaymentWidget();
    } catch (err) {
      alert("Could not open payment window. Please try again.");
    } finally {
      setRequeryingRRR(null);
    }
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!email) { setError("Please enter your email address."); return; }
    if (phone && !/^\d{6}$/.test(phone)) { setError("Phone suffix must be exactly 6 digits."); return; }
    setError(""); setLoading(true); setResults(null);
    try {
      const res = await axios.get(
        `https://api.uchselfservice.net/api/express/fetch-transactions/?email=${encodeURIComponent(email)}${phone ? `&digit=${phone}` : ""}`
      );
      const data = res.data.results || res.data || [];
      if (!Array.isArray(data) || data.length === 0) {
        setError("No transactions found for this email.");
      } else {
        setResults(data);
      }
    } catch {
      setError("Failed to fetch transactions. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const statusConfig = {
    completed: { icon: FaCheckCircle, color: "text-green-600", bg: "bg-green-50 border-green-200", label: "Completed" },
    success: { icon: FaCheckCircle, color: "text-green-600", bg: "bg-green-50 border-green-200", label: "Completed" },
    pending: { icon: FaClock, color: "text-yellow-600", bg: "bg-yellow-50 border-yellow-200", label: "Pending" },
    failed: { icon: FaTimesCircle, color: "text-red-600", bg: "bg-red-50 border-red-200", label: "Failed" },
  };

  const getStatus = (s) => (s || "").replace("PaymentStatus.", "").toLowerCase();
  const getMethod = (m) => m?.toLowerCase() === "remita" ? "QuickPay" : m || "—";

  return (
    <div className="min-h-screen bg-[#f5f5f0]">
      {/* Navbar */}
      <nav className="bg-white border-b border-gray-100 sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <a href="/" className="flex items-center gap-2.5">
            <div className="w-9 h-9 bg-white rounded-full flex items-center justify-center overflow-hidden shadow-sm border border-gray-100">
              <img src={logo} alt="UCH" className="w-7 h-7 object-contain" />
            </div>
            <div>
              <p className="font-bold text-gray-900 text-sm leading-tight">UCH Self Service</p>
              <p className="text-gray-400 text-[10px]">University College Hospital</p>
            </div>
          </a>
          <div className="flex items-center gap-3">
            <button onClick={() => navigate(-1)} className="text-sm text-gray-500 hover:text-[#0d3d2e] flex items-center gap-1.5">
              <FaArrowLeft className="w-3 h-3" /> Back
            </button>
            <button onClick={() => navigate("/login")} className="text-sm font-semibold text-white bg-[#0d3d2e] px-4 py-2 rounded-lg hover:bg-[#0a3226] transition">
              Sign in
            </button>
          </div>
        </div>
      </nav>

      <main className="max-w-3xl mx-auto px-4 py-12 sm:py-16">
        {/* Header */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 bg-green-50 border border-green-100 text-green-700 text-xs font-semibold px-4 py-1.5 rounded-full mb-4 uppercase tracking-wide">
            🔍 Transaction Lookup
          </div>
          <h1 className="text-3xl sm:text-4xl font-black text-gray-900 mb-3">Track Your Payments</h1>
          <p className="text-gray-500 text-sm sm:text-base max-w-md mx-auto">
            Enter your email to view your QuickPay transaction history and receipts.
          </p>
        </div>

        {/* Search Form */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 sm:p-8 mb-8">
          <form onSubmit={handleSearch} className="space-y-4">
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Email Address *</label>
              <input type="email" value={email} onChange={e => setEmail(e.target.value)} required
                placeholder="Enter the email used during payment"
                className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm bg-gray-50 focus:outline-none focus:ring-2 focus:ring-green-500 focus:bg-white transition" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Last 6 Digits of Phone (optional)</label>
              <input type="text" value={phone} onChange={e => setPhone(e.target.value.replace(/\D/g, "").slice(0, 6))}
                placeholder="e.g. 123456 — narrows results"
                maxLength={6}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm bg-gray-50 focus:outline-none focus:ring-2 focus:ring-green-500 focus:bg-white transition" />
            </div>
            {error && <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3 rounded-xl">{error}</div>}
            <button type="submit" disabled={loading}
              className="w-full bg-[#0d3d2e] hover:bg-[#0a3226] text-white font-semibold py-3.5 rounded-xl transition disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2">
              {loading ? <><FaSpinner className="animate-spin" /> Searching...</> : <><FaSearch /> Search Transactions</>}
            </button>
          </form>
        </div>

        {/* Results */}
        {results && (
          <div className="space-y-3">
            <p className="text-sm text-gray-500 mb-4"><strong>{results.length}</strong> transaction{results.length !== 1 ? "s" : ""} found</p>
            {results.map((tx, i) => {
              const status = getStatus(tx.status);
              const sc = statusConfig[status] || statusConfig.pending;
              const StatusIcon = sc.icon;
              const amount = parseFloat(tx.invoice_amount || tx.total_amount || tx.amount || 0);
              const patientName = `${tx.customer_meta?.patient_first_name || ""} ${tx.customer_meta?.patient_last_name || ""}`.trim() || "—";
              const services = (tx.items || []).map(getItemName).join(", ") || "—";
              return (
                <div key={tx.id || i} className="bg-white rounded-2xl border border-gray-100 overflow-hidden hover:shadow-md transition">
                  <div className="px-5 py-4 flex items-center justify-between gap-4">
                    <div className="flex items-center gap-4 min-w-0 flex-1">
                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 border ${sc.bg}`}>
                        <StatusIcon className={`w-5 h-5 ${sc.color}`} />
                      </div>
                      <div className="min-w-0">
                        <p className="font-semibold text-gray-900 text-sm truncate">{services}</p>
                        <p className="text-xs text-gray-400 mt-0.5">
                          {tx.created_at ? new Date(tx.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "—"}
                          &nbsp;·&nbsp;{getMethod(tx.payment_method)}
                        </p>
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="font-black text-gray-900 text-sm">₦{amount.toLocaleString("en-NG", { minimumFractionDigits: 2 })}</p>
                      <span className={`text-xs font-semibold px-2.5 py-1 rounded-full border ${sc.bg} ${sc.color}`}>{sc.label}</span>
                    </div>
                    <div className="flex gap-2 shrink-0">
                      {status === "pending" && tx.rrr && (
                        <button
                          onClick={() => openRemitaWithRRR(tx)}
                          disabled={requeryingRRR === tx.rrr}
                          className="text-xs text-white font-semibold bg-[#0d3d2e] border border-[#0d3d2e] px-3 py-1.5 rounded-lg hover:bg-[#0a3226] transition disabled:opacity-60 flex items-center gap-1.5">
                          {requeryingRRR === tx.rrr ? <FaSpinner className="animate-spin" /> : <FaRedo />}
                          Pay Now
                        </button>
                      )}
                      <button onClick={() => setSelected(selected?.id === tx.id ? null : tx)}
                        className="text-xs text-[#0d3d2e] font-semibold border border-green-200 px-3 py-1.5 rounded-lg hover:bg-green-50 transition">
                        {selected?.id === tx.id ? "Hide" : "Details"}
                      </button>
                    </div>
                  </div>

                  {/* Expanded details */}
                  {selected?.id === tx.id && (
                    <div className="border-t border-gray-50 px-5 py-4 bg-gray-50/50 space-y-3 text-sm">
                      <div className="grid grid-cols-2 gap-x-6 gap-y-2">
                        {[
                          ["Reference", tx.reference || "—"],
                          ["RRR", tx.rrr || "—"],
                          ["Patient", patientName],
                          ["Hospital No.", tx.customer_meta?.patient_hospital_number || "—"],
                          ["Patient Email", tx.customer_meta?.patient_email || "—"],
                          ["Patient Phone", tx.customer_meta?.patient_phone_number || "—"],
                          ["Payer", `${tx.customer_meta?.payer_first_name || ""} ${tx.customer_meta?.payer_last_name || ""}`.trim() || "—"],
                          ["Payer Email", tx.customer_meta?.payer_email || tx.customer_meta?.email || "—"],
                        ].map(([k, v]) => (
                          <div key={k}>
                            <p className="text-xs text-gray-400">{k}</p>
                            <p className="font-semibold text-gray-900 text-xs break-all">{v}</p>
                          </div>
                        ))}
                      </div>
                      {(tx.items || []).length > 0 && (
                        <div className="border-t border-gray-100 pt-3">
                          <p className="text-xs text-gray-400 mb-2">Services</p>
                          {(tx.items || []).map((item, j) => (
                            <div key={j} className="flex justify-between text-xs py-1 border-b border-gray-50 last:border-0">
                              <span className="text-gray-700">{getItemName(item)}</span>
                              <span className="font-semibold">₦{parseFloat(item.line_total || item.amount || 0).toLocaleString()}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Create wallet CTA */}
        <div className="mt-12 bg-[#0d3d2e] rounded-2xl p-7 text-center">
          <h3 className="text-white font-bold text-lg mb-2">Want easier payment tracking?</h3>
          <p className="text-green-200 text-sm mb-5">Create a free UCH wallet to see all your transactions in one place.</p>
          <button onClick={() => navigate("/register")}
            className="bg-white text-[#0d3d2e] font-semibold px-6 py-2.5 rounded-xl hover:bg-green-50 transition text-sm">
            Create Free Account
          </button>
        </div>
      </main>
    </div>
  );
}
